import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { Crown, Shield, User, Search, Award } from 'lucide-react';

/**
 * Admin component for managing Councillor and Mayor badges
 * Allows admins to assign/revoke official roles
 */
const RoleManagement = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const assignBadge = async (userId, badgeCode, badgeName) => {
    const key = `${userId}-${badgeCode}`;
    setProcessing({ ...processing, [key]: true });

    try {
      await api.post('/admin/badges/assign', {
        user_id: userId,
        badge_code: badgeCode
      });
      
      toast.success(`✅ Assigned ${badgeName} badge`);
      fetchUsers(); // Refresh to show updated badges
    } catch (error) {
      console.error('Error assigning badge:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign badge');
    } finally {
      setProcessing({ ...processing, [key]: false });
    }
  };

  const revokeBadge = async (userId, badgeCode, badgeName) => {
    const key = `${userId}-${badgeCode}`;
    setProcessing({ ...processing, [key]: true });

    try {
      await api.post('/admin/badges/revoke', {
        user_id: userId,
        badge_code: badgeCode
      });
      
      toast.success(`Revoked ${badgeName} badge`);
      fetchUsers(); // Refresh
    } catch (error) {
      console.error('Error revoking badge:', error);
      toast.error(error.response?.data?.detail || 'Failed to revoke badge');
    } finally {
      setProcessing({ ...processing, [key]: false });
    }
  };

  const hasBadge = (user, badgeCode) => {
    // Check in user's earned badges - backend returns 'code' from badge_definitions
    return user.user_badges?.some(b => b.code === badgeCode) || 
           user.badges?.some(b => b.badge_code === badgeCode || b.code === badgeCode);
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.ward?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort users: those with important roles first
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const getRolePriority = (user) => {
      const hasMayor = user.user_badges?.some(b => b.code === 'mayor');
      const hasCouncillor = user.user_badges?.some(b => b.code === 'councillor');
      const isAdmin = user.role === 'admin';
      const isModerator = user.role === 'community_leader';
      
      if (hasMayor) return 0;
      if (isAdmin) return 1;
      if (hasCouncillor) return 2;
      if (isModerator) return 3;
      return 4;
    };
    return getRolePriority(a) - getRolePriority(b);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A52A]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900/20 to-transparent border border-purple-500/30 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-2">
          <Crown className="w-6 h-6 text-purple-400" />
          <h3 className="font-headline text-xl text-white">ROLE MANAGEMENT</h3>
        </div>
        <p className="font-mono text-xs text-gray-400">
          Assign official Councillor and Mayor badges to elected officials
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
        <input
          type="text"
          placeholder="Search users by name, email, or ward..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg pl-10 pr-4 py-3 text-white font-mono text-sm focus:outline-none focus:border-[#C5A52A]"
        />
      </div>

      {/* Users Table */}
      <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#111] border-b border-[#1a1a1a]">
              <tr>
                <th className="px-4 py-3 text-left font-mono text-xs text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left font-mono text-xs text-gray-500 uppercase tracking-wider">
                  Ward
                </th>
                <th className="px-4 py-3 text-center font-mono text-xs text-gray-500 uppercase tracking-wider">
                  Admin
                </th>
                <th className="px-4 py-3 text-center font-mono text-xs text-gray-500 uppercase tracking-wider">
                  Moderator
                </th>
                <th className="px-4 py-3 text-center font-mono text-xs text-gray-500 uppercase tracking-wider">
                  Mayor
                </th>
                <th className="px-4 py-3 text-center font-mono text-xs text-gray-500 uppercase tracking-wider">
                  Councillor
                </th>
                <th className="px-4 py-3 text-center font-mono text-xs text-gray-500 uppercase tracking-wider">
                  Former Mayor
                </th>
                <th className="px-4 py-3 text-center font-mono text-xs text-gray-500 uppercase tracking-wider">
                  Former Councillor
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1a1a1a]">
              {sortedUsers.length > 0 ? (
                sortedUsers.map((user) => {
                  const isMayor = hasBadge(user, 'mayor');
                  const isCouncillor = hasBadge(user, 'councillor');
                  const isFormerMayor = hasBadge(user, 'former-mayor');
                  const isFormerCouncillor = hasBadge(user, 'former-councillor');
                  const isAdmin = user.role === 'admin';
                  const isModerator = user.role === 'community_leader';

                  return (
                    <tr key={user.id} className={`hover:bg-[#111] transition-colors ${
                      isMayor ? 'bg-purple-900/10' : 
                      isAdmin ? 'bg-red-900/10' : 
                      isCouncillor ? 'bg-blue-900/10' : 
                      isModerator ? 'bg-green-900/10' : ''
                    }`}>
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-mono text-sm text-white flex items-center gap-2">
                            {user.name}
                            {isMayor && <span className="text-purple-400 text-xs">👑</span>}
                            {isAdmin && <span className="text-red-400 text-xs">⚡</span>}
                          </div>
                          <div className="font-mono text-xs text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-gray-400">
                          {user.ward || '-'}
                        </span>
                      </td>

                      {/* Admin Role */}
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={async () => {
                            try {
                              await api.put(`/admin/users/${user.id}/role`, null, { 
                                params: { role: isAdmin ? 'user' : 'admin' } 
                              });
                              toast.success(isAdmin ? 'Removed admin role' : 'Assigned admin role');
                              fetchUsers();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to update role');
                            }
                          }}
                          data-testid={`toggle-admin-role-${user.id}`}
                          className={`px-3 py-1 rounded font-mono text-xs transition-colors ${
                            isAdmin
                              ? 'bg-red-500/20 border border-red-500/50 text-red-400 hover:bg-red-500/30'
                              : 'bg-gray-800 border border-gray-700 text-gray-500 hover:border-red-500/50 hover:text-red-400'
                          }`}
                        >
                          {isAdmin ? 'Revoke' : 'Assign'}
                        </button>
                      </td>

                      {/* Moderator Role */}
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={async () => {
                            try {
                              await api.put(`/admin/users/${user.id}/role`, null, { 
                                params: { role: isModerator ? 'user' : 'community_leader' } 
                              });
                              toast.success(isModerator ? 'Removed moderator role' : 'Assigned moderator role');
                              fetchUsers();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to update role');
                            }
                          }}
                          data-testid={`toggle-moderator-role-${user.id}`}
                          className={`px-3 py-1 rounded font-mono text-xs transition-colors ${
                            isModerator
                              ? 'bg-green-500/20 border border-green-500/50 text-green-400 hover:bg-green-500/30'
                              : 'bg-gray-800 border border-gray-700 text-gray-500 hover:border-green-500/50 hover:text-green-400'
                          }`}
                        >
                          {isModerator ? 'Revoke' : 'Assign'}
                        </button>
                      </td>

                      {/* Mayor Badge */}
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => isMayor 
                            ? revokeBadge(user.id, 'mayor', 'Mayor')
                            : assignBadge(user.id, 'mayor', 'Mayor')
                          }
                          disabled={processing[`${user.id}-mayor`]}
                          className={`px-3 py-1 rounded font-mono text-xs transition-colors ${
                            isMayor
                              ? 'bg-purple-500/20 border border-purple-500/50 text-purple-400 hover:bg-purple-500/30'
                              : 'bg-gray-800 border border-gray-700 text-gray-500 hover:border-purple-500/50 hover:text-purple-400'
                          }`}
                        >
                          {processing[`${user.id}-mayor`] ? '...' : isMayor ? 'Revoke' : 'Assign'}
                        </button>
                      </td>

                      {/* Councillor Badge */}
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => isCouncillor
                            ? revokeBadge(user.id, 'councillor', 'Councillor')
                            : assignBadge(user.id, 'councillor', 'Councillor')
                          }
                          disabled={processing[`${user.id}-councillor`]}
                          className={`px-3 py-1 rounded font-mono text-xs transition-colors ${
                            isCouncillor
                              ? 'bg-purple-500/20 border border-purple-500/50 text-purple-400 hover:bg-purple-500/30'
                              : 'bg-gray-800 border border-gray-700 text-gray-500 hover:border-purple-500/50 hover:text-purple-400'
                          }`}
                        >
                          {processing[`${user.id}-councillor`] ? '...' : isCouncillor ? 'Revoke' : 'Assign'}
                        </button>
                      </td>

                      {/* Former Mayor Badge */}
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => isFormerMayor
                            ? revokeBadge(user.id, 'former-mayor', 'Former Mayor')
                            : assignBadge(user.id, 'former-mayor', 'Former Mayor')
                          }
                          disabled={processing[`${user.id}-former-mayor`]}
                          className={`px-3 py-1 rounded font-mono text-xs transition-colors ${
                            isFormerMayor
                              ? 'bg-blue-500/20 border border-blue-500/50 text-blue-400 hover:bg-blue-500/30'
                              : 'bg-gray-800 border border-gray-700 text-gray-500 hover:border-blue-500/50 hover:text-blue-400'
                          }`}
                        >
                          {processing[`${user.id}-former-mayor`] ? '...' : isFormerMayor ? 'Revoke' : 'Assign'}
                        </button>
                      </td>

                      {/* Former Councillor Badge */}
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => isFormerCouncillor
                            ? revokeBadge(user.id, 'former-councillor', 'Former Councillor')
                            : assignBadge(user.id, 'former-councillor', 'Former Councillor')
                          }
                          disabled={processing[`${user.id}-former-councillor`]}
                          className={`px-3 py-1 rounded font-mono text-xs transition-colors ${
                            isFormerCouncillor
                              ? 'bg-blue-500/20 border border-blue-500/50 text-blue-400 hover:bg-blue-500/30'
                              : 'bg-gray-800 border border-gray-700 text-gray-500 hover:border-blue-500/50 hover:text-blue-400'
                          }`}
                        >
                          {processing[`${user.id}-former-councillor`] ? '...' : isFormerCouncillor ? 'Revoke' : 'Assign'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="px-4 py-12 text-center">
                    <User className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                    <p className="font-mono text-sm text-gray-500">
                      {searchTerm ? 'No users found matching your search' : 'No users to display'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-4">
        <h4 className="font-mono text-xs text-gray-500 uppercase tracking-wider mb-3">Badge Types</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-purple-500/50"></div>
            <span className="font-mono text-xs text-gray-400">Official (Current Role)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-blue-500/50"></div>
            <span className="font-mono text-xs text-gray-400">Legacy (Former Role)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleManagement;
